import React, { useState, useEffect } from 'react';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { diseasesApi, diseaseGroupsApi } from '../../api/diseases';
import { Disease, DiseaseGroup } from '../../types/diseases';
import {
  BrandModal,
  BrandModalBody,
  BrandModalFooter,
  BrandModalHeader,
} from '../ui/BrandModal';

interface CreateDiseaseDto {
  name: string;
  groupId: number;
  description?: string;
  cause?: string;
  symptoms?: string;
  diagnosis?: string;
  treatments?: string;
  prevention?: string;
  contagious?: boolean;
  severity?: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  mortality?: 'LOW' | 'MEDIUM' | 'HIGH';
  incubationPeriod?: string;
  notes?: string;
  affectedSystems?: string[];
}

interface AddDiseaseModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: CreateDiseaseDto) => Promise<void>;
}

const AddDiseaseModal: React.FC<AddDiseaseModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
}) => {
  const [formData, setFormData] = useState<CreateDiseaseDto>({
    name: '',
    groupId: 0,
    description: '',
    cause: '',
    symptoms: '',
    diagnosis: '',
    treatments: '',
    prevention: '',
    contagious: false,
    severity: 'MEDIUM',
    mortality: 'LOW',
    incubationPeriod: '',
    notes: '',
    affectedSystems: [],
  });
  const [groups, setGroups] = useState<DiseaseGroup[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const systemOptions = [
    'Respiratory',
    'Digestive',
    'Nervous',
    'Circulatory',
    'Reproductive',
    'Musculoskeletal',
    'Integumentary',
    'Urinary',
    'Immune',
    'Endocrine',
  ];

  useEffect(() => {
    if (isOpen) {
      loadGroups();
    }
  }, [isOpen]);

  const loadGroups = async () => {
    try {
      setLoading(true);
      const data = await diseaseGroupsApi.getAll();
      setGroups(data);
    } catch (error) {
      console.error('Error loading disease groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({
        ...prev,
        [name]: checked,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: name === 'groupId' ? parseInt(value) || 0 : value,
      }));
    }
  };

  const handleSystemChange = (system: string) => {
    setFormData(prev => ({
      ...prev,
      affectedSystems: prev.affectedSystems?.includes(system)
        ? prev.affectedSystems.filter(s => s !== system)
        : [...(prev.affectedSystems || []), system],
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name || !formData.groupId) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSubmitting(true);
      await onSubmit(formData);
      resetForm();
      onClose();
    } catch (error) {
      console.error('Error creating disease:', error);
      alert('Failed to create disease. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      groupId: 0,
      description: '',
      cause: '',
      symptoms: '',
      diagnosis: '',
      treatments: '',
      prevention: '',
      contagious: false,
      severity: 'MEDIUM',
      mortality: 'LOW',
      incubationPeriod: '',
      notes: '',
      affectedSystems: [],
    });
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!isOpen) return null;

  return (
    <BrandModal open={isOpen} onClose={handleClose} maxWidthClass="max-w-4xl">
      <BrandModalHeader
        title={
          <span className="inline-flex items-center">
            <ExclamationTriangleIcon className="h-6 w-6 text-orange-500 mr-2" />
            Add Disease
          </span>
        }
        onClose={handleClose}
      />
      <form onSubmit={handleSubmit}>
        <BrandModalBody className="space-y-4 max-h-96 overflow-y-auto">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {/* Disease Name */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Disease Name *
              </label>              <input
                type="text"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                placeholder="e.g., Newcastle Disease"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Disease Group */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Disease Group *
              </label>
              <select
                name="groupId"
                value={formData.groupId}
                onChange={handleInputChange}
                required
                disabled={loading}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option value={0}>Select a group...</option>
                {groups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Severity */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Severity
              </label>
              <select
                name="severity"
                value={formData.severity}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="LOW-1753461297297" value="LOW">Low</option>
                <option key="MEDIUM-1753461297297" value="MEDIUM">Medium</option>
                <option key="HIGH-1753461297297" value="HIGH">High</option>
                <option key="CRITICAL-1753461297297" value="CRITICAL">Critical</option>
              </select>
            </div>

            {/* Mortality */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Mortality Rate
              </label>
              <select
                name="mortality"
                value={formData.mortality}
                onChange={handleInputChange}
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              >
                <option key="LOW-1753461297297" value="LOW">Low</option>
                <option key="MEDIUM-1753461297297" value="MEDIUM">Medium</option>
                <option key="HIGH-1753461297297" value="HIGH">High</option>
              </select>
            </div>

            {/* Incubation Period */}
            <div>
              <label className="block text-sm font-medium text-slate-700">
                Incubation Period
              </label>
              <input
                type="text"
                name="incubationPeriod"
                value={formData.incubationPeriod}
                onChange={handleInputChange}
                placeholder="e.g., 3-7 days"
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Contagious */}
            <div className="flex items-center">
              <input
                type="checkbox"
                name="contagious"
                checked={formData.contagious}
                onChange={handleInputChange}
                className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
              />
              <label className="ml-2 block text-sm text-slate-900">
                Contagious
              </label>
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Description
              </label>
              <textarea
                name="description"
                value={formData.description}
                onChange={handleInputChange}
                rows={3}
                placeholder="Brief description of the disease..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Cause */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Cause
              </label>
              <textarea
                name="cause"
                value={formData.cause}
                onChange={handleInputChange}
                rows={2}
                placeholder="What causes this disease (bacteria, virus, parasite, etc.)..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Symptoms */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Symptoms
              </label>
              <textarea
                name="symptoms"
                value={formData.symptoms}
                onChange={handleInputChange}
                rows={3}
                placeholder="Clinical signs and symptoms to watch for..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Diagnosis */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Diagnosis
              </label>
              <textarea
                name="diagnosis"
                value={formData.diagnosis}
                onChange={handleInputChange}
                rows={3}
                placeholder="How to diagnose this disease..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Treatments */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Treatments
              </label>
              <textarea
                name="treatments"
                value={formData.treatments}
                onChange={handleInputChange}
                rows={3}
                placeholder="Treatment options and protocols..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Prevention */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Prevention
              </label>
              <textarea
                name="prevention"
                value={formData.prevention}
                onChange={handleInputChange}
                rows={3}
                placeholder="Prevention measures and biosecurity protocols..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>

            {/* Affected Systems */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Affected Body Systems
              </label>
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {systemOptions.map((system) => (
                  <div key={system} className="flex items-center">
                    <input
                      type="checkbox"
                      id={`system-${system}`}
                      checked={formData.affectedSystems?.includes(system) || false}
                      onChange={() => handleSystemChange(system)}
                      className="h-4 w-4 text-cyan-600 focus:ring-cyan-500 border-gray-300 rounded"
                    />
                    <label htmlFor={`system-${system}`} className="ml-2 text-sm text-slate-700">
                      {system}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Notes */}
            <div className="sm:col-span-2">
              <label className="block text-sm font-medium text-slate-700">
                Additional Notes
              </label>
              <textarea
                name="notes"
                value={formData.notes}
                onChange={handleInputChange}
                rows={3}
                placeholder="Any additional information about the disease..."
                className="mt-1 block w-full border-gray-300 rounded-md shadow-sm focus:ring-cyan-500 focus:border-cyan-500"
              />
            </div>
          </div>

        </BrandModalBody>
        <BrandModalFooter>
          <button
            type="button"
            onClick={handleClose}
            disabled={submitting}
            className="btn btn-secondary text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={submitting}
            className="btn btn-primary text-sm disabled:opacity-50"
          >
            {submitting ? 'Adding...' : 'Add Disease'}
          </button>
        </BrandModalFooter>
      </form>
    </BrandModal>
  );
};

export default AddDiseaseModal; 